import {
  createCodec,
  unwrapSpaceKey,
  wrappedKeySchema,
  type Codec,
  type EncryptionMode,
  type WrappedKey,
} from "@vmeste/shared";
import { forgetPlaintext, rememberPlaintext } from "./plaintext.js";

/**
 * Where a space key lives on this device.
 *
 * IndexedDB, and not `localStorage`, for two reasons that both matter:
 *
 *   1. `localStorage` holds strings. A `CryptoKey` is not a string, and making
 *      it one means making it extractable — which throws away the guarantee
 *      that a script on the page cannot lift the key out and post it somewhere.
 *      IndexedDB stores the key object itself, still non-extractable.
 *   2. **The service worker can read it.** A push arrives with nobody looking,
 *      and the notification's text is a blob only this key opens. `push.ts` has
 *      said since it was written that decryption happens there; this is the
 *      store it reads.
 *
 * The key never leaves the device by any path. What travels — to a second
 * phone, through the server — is the *wrapped* blob, which needs the
 * passphrase; see `spacekey.ts` in `@vmeste/shared`.
 */

const DB_NAME = "vmeste-keys";
const DB_VERSION = 1;
const STORE = "space-keys";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is unavailable"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("could not open the key store"));
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>) {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = work(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("key store failed"));
        tx.oncomplete = () => db.close();
      }),
  );
}

/* ------------------------------------------------------- who is watching */

/**
 * The store says when it changed, and everybody asking re-reads.
 *
 * Without this, "does this device have a key" was answered once per component
 * and never again. The screen that turned encryption on knew the key had just
 * landed; the layout wrapped around it did not, and went on believing the space
 * was locked — so the owner was shown a lock screen for a space they had just
 * unlocked, on the device holding the key, and asked for the recovery code they
 * had been given ten seconds earlier.
 *
 * The same module-level store the attachments and the unfurls use, for the same
 * reason: this is one fact about the device, not a fact each component owns a
 * copy of.
 */
const listeners = new Set<() => void>();
let version = 0;

export function subscribeToKeys(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function keysVersion(): number {
  return version;
}

function keysChanged(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** The key for a space, or null when this device has never been unlocked for it. */
export async function loadSpaceKey(spaceId: string): Promise<CryptoKey | null> {
  try {
    return (await run<CryptoKey | undefined>("readonly", (s) => s.get(spaceId))) ?? null;
  } catch {
    // Private browsing, storage denied, a broken database. The space reads as
    // locked, which is the honest answer and not a crash on boot.
    return null;
  }
}

export async function saveSpaceKey(spaceId: string, key: CryptoKey): Promise<void> {
  await run("readwrite", (s) => s.put(key, spaceId));
  keysChanged();
}

/**
 * Forget the key for one space, or all of them.
 *
 * Signing out has to do this. A key left in IndexedDB on a shared computer,
 * next to a synced copy of the ciphertext, is the whole encryption undone by
 * the one action a person takes precisely because they are worried about it.
 */
export async function forgetSpaceKeys(spaceId?: string): Promise<void> {
  forgetPlaintext();
  try {
    await run("readwrite", (s) => (spaceId ? s.delete(spaceId) : s.clear()));
  } catch {
    // Nothing to forget, or nowhere to forget it from.
  }
  // Announced even when the delete threw: a device that cannot reach its key
  // store has no key, and every screen has to hear that too.
  keysChanged();
}

/* ------------------------------------------------------------- unlocking */

/** Unwrap with the passphrase and remember the key on this device. */
export async function unlockSpace(
  spaceId: string,
  wrapped: WrappedKey,
  passphrase: string,
): Promise<CryptoKey> {
  const key = await unwrapSpaceKey(wrappedKeySchema.parse(wrapped), passphrase);
  await saveSpaceKey(spaceId, key);
  return key;
}

/* ---------------------------------------------------------------- codecs */

/**
 * The codec a space is read and written with, held per space.
 *
 * Cached because every screen asks for it on every render, and because a codec
 * is a closure over a key rather than something with state of its own.
 *
 * A space in `e2ee` mode whose key this device does not have gets **no codec at
 * all** — `null`, not a plaintext one. Falling back to plaintext would write
 * readable content into a space that is supposed to be encrypted, which is a
 * far worse failure than refusing to write.
 */
const codecs = new Map<string, Codec>();

export function codecFor(spaceId: string, mode: EncryptionMode): Codec | null {
  if (mode === "none") {
    let codec = codecs.get(spaceId);
    if (!codec || codec.mode !== "none") {
      codec = createCodec("none");
      codecs.set(spaceId, codec);
    }
    return codec;
  }

  const existing = codecs.get(spaceId);
  return existing && existing.mode === "e2ee" ? existing : null;
}

/**
 * Called once a key is in hand — on unlock, and on boot for a stored key.
 *
 * The codec is wrapped so that encoding also files the plaintext away. The
 * client knows what it just wrote; without this the row it created would show a
 * placeholder until the server echoed the ciphertext back and it was decrypted
 * again — a visible stutter on every keystroke-to-screen path, and completely
 * avoidable, because the answer was in hand a moment ago.
 */
export function useKeyFor(spaceId: string, key: CryptoKey): Codec {
  const inner = createCodec("e2ee", key);

  const codec: Codec = {
    mode: inner.mode,
    async encode(value) {
      const blob = await inner.encode(value);
      rememberPlaintext(blob, value ?? null);
      return blob;
    },
    decode: inner.decode,
  };

  codecs.set(spaceId, codec);
  return codec;
}

export function forgetCodecs(): void {
  codecs.clear();
}

/**
 * Load whatever key this device already has and build its codec.
 *
 * Called on boot, before the first delta is applied, so that an encrypted space
 * this device is already unlocked for renders straight away rather than showing
 * placeholders until somebody types a passphrase again.
 */
export async function restoreSpaceCodec(
  spaceId: string,
  mode: EncryptionMode,
): Promise<Codec | null> {
  if (mode === "none") return codecFor(spaceId, "none");

  const key = await loadSpaceKey(spaceId);
  return key ? useKeyFor(spaceId, key) : null;
}
